import React, { useState, useEffect } from "react";
import { useGetKycRecordsQuery } from "../../redux/slices/kyc/kycApi";
import { Header, ExportCSVButton, Pagination } from "../../components";
import ImageModal from "../../components/ImageModal";
import SearchBox from "../../components/SearchBox";
import { useStateContext } from "../../contexts/ContextProvider";

const KycVerifiedPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState({
    src: "",
    alt: "",
    record: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sortField, setSortField] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");

  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 500); 

    return () => clearTimeout(timer);
  }, [searchTerm]);

  
  const { isLoading, error, data } = useGetKycRecordsQuery({
    page: currentPage,
    search: debouncedSearchTerm,
  });

  const { currentMode } = useStateContext();

  
  const kycRecords = data?.data || [];
  const totalRecords = data?.total || 0;
  const backendLimit = data?.limit || 10; // Use backend's limit
  const totalPages = Math.ceil(totalRecords / backendLimit);

  
  const sortRecords = (records, field, direction) =>
    [...records].sort((a, b) => {
      let aValue = a[field];
      let bValue = b[field];

      
      if (aValue === null || aValue === undefined) aValue = "";
      if (bValue === null || bValue === undefined) bValue = "";

      
      if (
        field === "createdAt" ||
        field === "updatedAt" ||
        field === "verifiedAt"
      ) {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }

      
      if (typeof aValue === "string" && aValue !== "") {
        aValue = aValue.toLowerCase();
      }
      if (typeof bValue === "string" && bValue !== "") {
        bValue = bValue.toLowerCase();
      }

      if (direction === "asc") {
        if (aValue > bValue) return 1;
        if (aValue < bValue) return -1;
        return 0;
      }
      if (aValue < bValue) return 1;
      if (aValue > bValue) return -1;
      return 0;
    });

  
  const sortedRecords = sortRecords(kycRecords, sortField, sortDirection);

  
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1); 
  };

  
  const getSortIndicator = (field) => {
    if (sortField !== field) {
      return (
        <svg
          className="w-4 h-4 ml-1 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      );
    }
    return sortDirection === "asc" ? (
      <svg
        className="w-4 h-4 ml-1 text-blue-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 15l7-7 7 7"
        />
      </svg>
    ) : (
      <svg
        className="w-4 h-4 ml-1 text-blue-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    );
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value.trim());
    setCurrentPage(1); 
  };

  
  

  


  const openImageModal = (src, alt, record) => {
    setModalImage({ src, alt, record });
    setModalOpen(true);
  };
  const closeImageModal = () => setModalOpen(false);

  if (isLoading) {
    return (
      <div
        className={`m-2 md:m-10 mt-24 p-2 md:p-10 rounded-3xl ${
          currentMode === "Dark" ? "bg-secondary-dark-bg" : "bg-white"
        }`}
      >
        <Header category="Page" title="KYC Verified Records" />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`m-2 md:m-10 mt-24 p-2 md:p-10 rounded-3xl ${
          currentMode === "Dark" ? "bg-secondary-dark-bg" : "bg-white"
        }`}
      >
        <Header category="Page" title="KYC Verified Records" />
        <div className="flex items-center justify-center py-16">
          <div
            className={`text-center rounded-lg p-8 max-w-md mx-auto border ${
              currentMode === "Dark"
                ? "bg-gray-800 border-gray-600"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="mb-4">
              <svg
                className="w-16 h-16 mx-auto text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h3
              className={`text-lg font-semibold mb-2 ${
                currentMode === "Dark" ? "text-white" : "text-gray-700"
              }`}
            >
              Unable to Load KYC Records
            </h3>
            <p
              className={`text-sm mb-4 ${
                currentMode === "Dark" ? "text-gray-300" : "text-gray-500"
              }`}
            >
              We encountered an issue while loading the KYC verification data.
              Please try refreshing the page or contact support if the problem
              persists.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    if (status === "auto_approved") return "bg-green-100 text-green-800";
    if (status === "auto_declined") return "bg-red-100 text-red-800";
    return "bg-yellow-100 text-yellow-800";
  };

  const getStatusLabel = (status) => {
    if (status === "auto_approved") return "KYC APPROVED";
    if (status === "auto_declined") return "KYC DECLINED";
    return status;
  };

  const getRowBackgroundClass = (index) => {
    if (currentMode === "Dark") {
      return index % 2 === 0
        ? "bg-secondary-dark-bg"
        : "bg-secondary-dark-bg-light";
    }
    return index % 2 === 0 ? "bg-white" : "bg-gray-50";
  };

  return (
    <div
      className={`m-2 md:m-10 mt-24 p-2 md:p-10 rounded-3xl ${
        currentMode === "Dark" ? "bg-secondary-dark-bg" : "bg-white shadow-lg"
      }`}
    >
      <Header category="Page" title="KYC Verified Records" />
      <ImageModal
        isOpen={modalOpen}
        onClose={closeImageModal}
        imageSrc={modalImage.src}
        alt={modalImage.alt}
        record={modalImage.record}
      />
      {/* Search Box and Export Button */}
      <div className="mb-4 flex items-center justify-between">
        <SearchBox
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by Email"
        />
        <ExportCSVButton
          data={sortedRecords}
          filename="kyc-records.xlsx"
          buttonText="Export to Excel"
        />
      </div>





      {/* Table */}
      <div className="overflow-x-auto">
        <table
          className={`min-w-max border ${
            currentMode === "Dark"
              ? "bg-secondary-dark-bg border-gray-600"
              : "bg-white border-gray-300"
          }`}
        >
          <thead
            className={
              currentMode === "Dark"
                ? "bg-secondary-dark-bg-light"
                : "bg-gray-50"
            }
          >
            <tr>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("userId")}
              >
                <div className="flex items-center">
                  User ID
                  {getSortIndicator("userId")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("user.email")}
              >
                <div className="flex items-center">
                  Email
                  {getSortIndicator("user.email")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("transactionId")}
              >
                <div className="flex items-center">
                  Transaction ID
                  {getSortIndicator("transactionId")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("status")}
              >
                <div className="flex items-center">
                  Status
                  {getSortIndicator("status")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("provider")}
              >
                <div className="flex items-center">
                  Provider
                  {getSortIndicator("provider")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("id")}
              >
                <div className="flex items-center">
                  ID
                  {getSortIndicator("id")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("digilockerAddress")}
              >
                <div className="flex items-center">
                  Digilocker Address
                  {getSortIndicator("digilockerAddress")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("digilockerDob")}
              >
                <div className="flex items-center">
                  Digilocker Dob
                  {getSortIndicator("digilockerDob")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("poiName")}
              >
                <div className="flex items-center">
                  Poi Name
                  {getSortIndicator("poiName")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600"
                    : "text-gray-500 border-gray-300"
                }`}
              >
                ID Photo
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600"
                    : "text-gray-500 border-gray-300"
                }`}
              >
                Face Image
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600"
                    : "text-gray-500 border-gray-300"
                }`}
              >
                POI Image
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("verifiedAt")}
              >
                <div className="flex items-center">
                  Verified At
                  {getSortIndicator("verifiedAt")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("createdAt")}
              >
                <div className="flex items-center">
                  Created At
                  {getSortIndicator("createdAt")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("updatedAt")}
              >
                <div className="flex items-center">
                  Updated At
                  {getSortIndicator("updatedAt")}
                </div>
              </th>
            </tr>
          </thead>
          <tbody
            className={`divide-y ${
              currentMode === "Dark"
                ? "bg-secondary-dark-bg divide-gray-600"
                : "bg-white divide-gray-200"
            }`}
          >
            {sortedRecords.map((record, index) => (
              <tr key={record.id} className={getRowBackgroundClass(index)}>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                    currentMode === "Dark" ? "text-white" : "text-gray-900"
                  }`}
                >
                  {record.userId}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {record.user?.email || "N/A"}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {record.transactionId}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                      record.status
                    )}`}
                  >
                    {getStatusLabel(record.status)}
                  </span>
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {record.provider}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {record.id}
                </td>
                <td
                  className={`px-6 py-4 text-sm break-words align-middle max-w-xs line-clamp-2 ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                  title={record.digilockerAddress}
                >
                  {record.digilockerAddress}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {record.digilockerDob}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {record.poiName}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {record.digilockerIdPhoto ? (
                    <button
                      type="button"
                      onClick={() =>
                        openImageModal(
                          `data:image/jpeg;base64,${record.digilockerIdPhoto}`,
                          `Digilocker ID of ${record.userId || "user"}`,
                          record
                        )
                      }
                      className="focus:outline-none"
                    >
                      <img
                        src={`data:image/jpeg;base64,${record.digilockerIdPhoto}`}
                        alt={`Digilocker ID of ${record.userId || "user"}`}
                        style={{
                          width: 40,
                          height: 40,
                          objectFit: "cover",
                          borderRadius: "4px",
                        }}
                      />
                    </button>
                  ) : (
                    "No Photo"
                  )}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {record.faceImagePath ? (
                    <button
                      type="button"
                      onClick={() =>
                        openImageModal(
                          record.faceImagePath,
                          `Face of ${record.userId || "user"}`,
                          record
                        )
                      }
                      className="focus:outline-none"
                    >
                      <img
                        src={record.faceImagePath}
                        alt={`Face of ${record.userId || "user"}`}
                        style={{
                          width: 40,
                          height: 40,
                          objectFit: "cover",
                          borderRadius: "4px",
                        }}
                      />
                    </button>
                  ) : (
                    "No Photo"
                  )}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {record.poiImagePath ? (
                    <button
                      type="button"
                      onClick={() =>
                        openImageModal(
                          record.poiImagePath,
                          `POI of ${record.userId || "user"}`,
                          record
                        )
                      }
                      className="focus:outline-none"
                    >
                      <img
                        src={record.poiImagePath}
                        alt={`POI of ${record.userId || "user"}`}
                        style={{
                          width: 40,
                          height: 40,
                          objectFit: "cover",
                          borderRadius: "4px",
                        }}
                      />
                    </button>
                  ) : (
                    "No Photo"
                  )}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {record.verifiedAt
                    ? new Date(record.verifiedAt).toLocaleString()
                    : ""}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {record.createdAt
                    ? new Date(record.createdAt).toLocaleString()
                    : ""}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {record.updatedAt
                    ? new Date(record.updatedAt).toLocaleString()
                    : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        totalRecords={totalRecords}
        backendLimit={backendLimit}
        recordsCount={sortedRecords.length}
      />

      {/* No Records Message */}
      {sortedRecords.length === 0 && (
        <div className="flex items-center justify-center py-16">
          <div
            className={`text-center rounded-lg p-8 max-w-md mx-auto border ${
              currentMode === "Dark"
                ? "bg-gray-800 border-gray-600"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="mb-4">
              <svg
                className={`w-16 h-16 mx-auto ${
                  currentMode === "Dark" ? "text-gray-500" : "text-gray-400"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h3
              className={`text-lg font-semibold mb-2 ${
                currentMode === "Dark" ? "text-white" : "text-gray-700"
              }`}
            >
              No KYC Records Found
            </h3>
            <p
              className={`text-sm ${
                currentMode === "Dark" ? "text-gray-300" : "text-gray-500"
              }`}
            >
              {searchTerm
                ? "No records match your search criteria. Try adjusting your search terms."
                : "No KYC records available at the moment."}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default KycVerifiedPage;
